/**
 * CE QU'ON FAIT D'UN TAS D'ANNOTATIONS — filtrer, trier, compter.
 *
 * Des fonctions pures, sans React et sans DOM, pour la même raison que la géométrie des
 * hexagones : la carte est le composant le plus long à mettre au point (design §6), et tout ce
 * qu'on peut en prouver sans monter un écran est autant de gagné.
 *
 * LES AFFAIRES EN SUSPENS SONT LE VRAI OUTIL (§4.2). Une aventure de mille nœuds ne se navigue pas
 * en se souvenant : « la porte verrouillée du marais », trois heures et deux régions plus tard, ne
 * se retrouve pas autrement qu'en la lisant dans une liste. C'est pour ça qu'une annotation se
 * RÉSOUT au lieu de s'effacer — la liste doit maigrir quand on avance, sinon elle ne dit plus rien.
 */
import type { Annotation, Marqueur } from "../api/types.js";
import { MARQUEURS } from "../api/types.js";

/** Ce que le filtre laisse passer. `null` — aucun filtre — laisse tout : ne rien cocher et tout
 * cocher doivent donner la même carte, sans quoi le filtre efface la carte au premier clic. */
export function filtrees(annotations: readonly Annotation[], marqueurs: ReadonlySet<Marqueur> | null): Annotation[] {
  if (!marqueurs || marqueurs.size === 0) return [...annotations];
  return annotations.filter((a) => marqueurs.has(a.marqueur));
}

/**
 * LES AFFAIRES EN SUSPENS : ce qui n'est pas résolu, dans l'ordre où l'on veut le lire. Les
 * marqueurs qui appellent une ACTION viennent d'abord — une porte verrouillée, une clef, une
 * énigme —, puis les avertissements, puis le reste. Une liste triée par date est une liste où ce
 * qu'on doit faire se noie sous ce qu'on a noté en passant.
 */
const URGENCE: Record<Marqueur, number> = {
  porte: 0,
  clef: 1,
  enigme: 2,
  revisiter: 3,
  pnj: 4,
  coffre: 5,
  ressource: 6,
  monstre: 7,
  danger: 8
};

export function enSuspens(annotations: readonly Annotation[]): Annotation[] {
  return annotations
    .filter((a) => !a.resolue)
    .sort((a, b) => URGENCE[a.marqueur] - URGENCE[b.marqueur] || a.posee.localeCompare(b.posee));
}

/** Combien de notes par marqueur : ce que le filtre affiche à côté de chaque case à cocher. Un
 * filtre qui propose « coffre (0) » dit au moins qu'il n'y en a pas, ce qui est une information. */
export function comptesParMarqueur(annotations: readonly Annotation[]): Record<Marqueur, number> {
  const comptes = Object.fromEntries(MARQUEURS.map((m) => [m, 0])) as Record<Marqueur, number>;
  for (const note of annotations) comptes[note.marqueur] += 1;
  return comptes;
}

/* --- Lire ce qui est écrit sur une case ------------------------------------------- */

export interface CeQuiEstEcrit {
  clef: string;
  /** Le nom du lieu, quand la case a été visitée. Aperçue de loin, elle n'a que sa clef. */
  nom: string | null;
  miennes: Annotation[];
  desAutres: Annotation[];
}

/**
 * TOUT CE QUI EST ÉCRIT SUR UNE CASE — les siennes d'abord, celles des autres ensuite.
 *
 * Le FILTRE NE S'APPLIQUE PAS ICI, et c'est délibéré : le filtre répond à « que dessine-t-on ? »,
 * le cartouche répond à « qu'y a-t-il d'écrit ici ? ». Un cartouche qui dirait « rien » sur une
 * case portant une note qu'on a simplement décochée serait un cartouche qui ment — c'est la même
 * raison qui garde les notes filtrées dans la liste des affaires en suspens (design §3).
 */
export function ceQuiEstEcritSur(
  annotations: { miennes: readonly Annotation[]; calque: readonly Annotation[] },
  clef: string | null,
  noms?: ReadonlyMap<string, string>
): CeQuiEstEcrit | null {
  if (!clef) return null;
  return {
    clef,
    nom: noms?.get(clef) ?? null,
    miennes: annotations.miennes.filter((a) => a.hexClef === clef),
    desAutres: annotations.calque.filter((a) => a.hexClef === clef)
  };
}

/** Y a-t-il seulement quelque chose à lire ? Une question à part, parce que le cartouche s'affiche
 * QUAND MÊME sur une case vide : « rien d'écrit ici » est une réponse, et une case survolée qui ne
 * répond rien du tout ferait croire à un survol qui n'a pas pris. */
export function riennEstEcrit(ecrit: CeQuiEstEcrit | null): boolean {
  return !ecrit || (ecrit.miennes.length === 0 && ecrit.desAutres.length === 0);
}
